/**
 * Quick Pay — a one-screen, one-time card/ACH payment for walk-in and call-in customers whose
 * invoice the ERP raises late. Mirrors FTE's `models/quickPay.ts` and the `/quick-pay`,
 * `/transaction/quick-pay` and `/payments/{id}/quick-pay/*` endpoints.
 */

import { resolveCostOfCapitalPercent } from 'features/freely-pay/utils/cost-of-capital-display';

export type QuickPayStatus = 'Unallocated' | 'Allocated';

export interface QuickPayLookupCustomer {
  customerId: string;
  companyAccountNumber: string | null;
  displayName: string | null;
  inactive: boolean;
  /** Customer-level Cost of Capital overrides; unset inherits the profile. */
  enableCostOfCapital?: boolean;
  costOfCapitalPercent?: number | null;
}

export interface QuickPayLookupInvoice {
  invoiceRef: string;
  customerId: string;
  customerAccountNumber: string | null;
  outstandingAmount: number;
  totalAmount: number;
  dueDate: string | null;
  paymentStatus: string | null;
  payable: boolean;
  refusal: string | null;
}

export interface QuickPayLookupResult {
  defaultCustomer: QuickPayLookupCustomer;
  customer: QuickPayLookupCustomer | null;
  invoice: QuickPayLookupInvoice | null;
  invoiceBelongsToCustomer: boolean;
  classification: { customerMatched: boolean; invoiceMatched: boolean; status: QuickPayStatus | null };
}

export interface QuickPayRequest {
  customerNumber: string;
  invoiceNumber: string;
  amount: number;
  comment?: string | null;
  encryptedPaymentAccount: string;
}

export interface UnallocatedQuickPay {
  freelyPaymentId: string;
  transactionId: string | null;
  paymentDateTime: string;
  /** What was charged — cash plus any cost of capital. */
  amount: number;
  /** Cost of capital FTE priced on the card; stays the Fee line, never allocated. */
  costOfCapitalAmount?: number;
  /** What an allocation places: `amount − costOfCapitalAmount`. Absent on rows from an older FTE. */
  cashAmount?: number;
  paymentMethod: string;
  cardType: string | null;
  paymentMethodDetail: string | null;
  status: string;
  customerId: string | null;
  customerAccountNumber: string | null;
  customerName: string | null;
  enteredCustomerNumber: string;
  enteredInvoiceNumber: string;
  comment: string | null;
  customerMatched: boolean;
  suggestedCustomer: { customerId: string; companyAccountNumber: string | null; name: string | null } | null;
  invoiceNowExists: {
    invoiceRef: string;
    customerId: string;
    customerAccountNumber: string | null;
    outstandingAmount: number;
  } | null;
}

export interface UnallocatedQuickPayPage {
  rows: UnallocatedQuickPay[];
  count: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface QuickPayAllocationBody {
  customerId: string;
  lines: { invoiceRef: string; amount: number }[];
  onAccountAmount?: number | null;
  reason?: string | null;
  fingerprint?: string | null;
}

export interface QuickPayAllocationPreview {
  freelyPaymentId: string;
  requestProblems: { code: string; message: string }[];
  adjustment: {
    problems: { code: string; message: string; invoiceRef?: string | null }[];
    fingerprint: string;
    changes?: { documentDeltas?: { invoiceRef: string; delta: number }[] };
  } | null;
}

/**
 * What the operator is told about a lookup, before they charge. Pure so the screen and its spec read
 * one rule: `null` means fully matched — an ordinary payment that needs no allocating later.
 */
export function quickPayNotice(r: QuickPayLookupResult | null, typedCustomer: string, typedInvoice: string): string | null {
  if (!r || r.classification.status === null) return null;
  const typedC = typedCustomer.trim();
  const typedI = typedInvoice.trim();
  const reasons: string[] = [];
  if (!r.classification.customerMatched) {
    reasons.push(typedC && !r.customer ? `Customer "${typedC}" was not found.` : 'No specific customer was entered.');
  }
  if (!r.classification.invoiceMatched && typedI) {
    if (!r.invoice) reasons.push(`Invoice "${typedI}" is not on file yet.`);
    else if (!r.invoice.payable) reasons.push(`Invoice ${r.invoice.invoiceRef} cannot take a payment (${r.invoice.refusal ?? 'not payable'}).`);
    else if (!r.invoiceBelongsToCustomer) reasons.push(`Invoice ${r.invoice.invoiceRef} belongs to a different customer.`);
  }
  const who = r.classification.customerMatched
    ? (r.customer?.displayName || r.customer?.companyAccountNumber || 'this customer')
    : (r.defaultCustomer.displayName || r.defaultCustomer.companyAccountNumber || 'the default customer');
  return [
    ...reasons,
    `The payment can still be taken: it will be recorded against ${who} and listed as Unallocated until it is allocated on the A/R dashboard.`,
  ].join(' ');
}

/** Why cost of capital does or does not apply to this Quick Pay — the preview's own words. */
export type QuickPayCocReason = 'credit' | 'off' | 'ach' | 'debit' | 'unverified' | 'blocked' | 'no-amount';

export interface QuickPayCostOfCapital {
  applies: boolean;
  reason: QuickPayCocReason;
  percent: number;
  amount: number;
  /** What the card will be charged: the amount typed plus `amount`. */
  total: number;
}

/**
 * The cost of capital FTE will add to this Quick Pay, shown before charging. FTE prices it itself
 * (`computeCostOfCapital`) from the gateway's card metadata, so this mirrors its rule rather than
 * deciding anything: a CREDIT card only (debit, prepaid and an unverified card get none), the
 * profile's percent or card-type override, never in a blocked billing state, rounded to cents the
 * way FTE rounds (`Math.round`, not the Collect calculator's floor).
 */
export function quickPayCostOfCapital(
  settings: {
    enableCostOfCapital?: boolean;
    costOfCapitalPercent?: number;
    enableCostOfCapitalByCardType?: boolean;
    costOfCapitalCardTypeOverrides?: Record<string, number>;
  } | null | undefined,
  card: { paymentType?: string | null; cardFunding?: string | null; cardType?: string | null },
  amount: number,
  blockedState: boolean
): QuickPayCostOfCapital {
  const net = Number(amount) > 0 ? Math.round(Number(amount) * 100) / 100 : 0;
  const none = (reason: QuickPayCocReason, percent = 0): QuickPayCostOfCapital => ({
    applies: false,
    reason,
    percent,
    amount: 0,
    total: net,
  });
  if (!settings?.enableCostOfCapital) return none('off');
  if ((card.paymentType ?? 'CARD').toUpperCase() === 'ACH') return none('ach');
  const percent = resolveCostOfCapitalPercent(settings as any, card.cardType);
  const funding = (card.cardFunding ?? '').toUpperCase();
  if (funding !== 'CREDIT') return none(funding ? 'debit' : 'unverified', percent);
  if (blockedState) return none('blocked', percent);
  if (net <= 0 || percent <= 0) return none('no-amount', percent);
  const coc = Math.round(net * (percent / 100) * 100) / 100;
  return { applies: true, reason: 'credit', percent, amount: coc, total: Math.round((net + coc) * 100) / 100 };
}
